'use client';

import { c as _c } from "react/compiler-runtime";
import type { PullRequestDemo } from '@/lib/code-sessions/pull-request-demo/demo-state';
import { ChevronRight, Play } from 'lucide-react';
import Link from 'next/link';
import { SIDEBAR_HEADING_CLASS, SIDEBAR_HEADING_VALUE_CLASS } from '../shared/sidebar-heading-classes';
import { PrV2DemoInternalPill, PrV2DemoProgress } from './pr-v2-demo-card';
import { PR_V2_DEMO_CARD_ID, usePrV2DemoCollapsed } from './use-pr-v2-demo-collapsed';
import { BetterTooltip } from '@/components/ui/tooltip';
import { usePrV2Demo } from '@/data/v2/pr/use-pr-v2-demo';
import { cn } from '@/lib/utils';
type PrV2DemoStatusProps = {
  owner: string;
  repo: string;
  number: string;
  installationId: number | null;
  className?: string;
};

// Laid out like the Checks heading row: name and › on the left, status on the right.
const ROW_CLASS = 'group/demo flex w-full items-center justify-between gap-3 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The row's accessible name in each state; `PrV2DemoProgress` says the status on its right. */
const ROW_LABELS: Record<PullRequestDemo['state'], string> = {
  recording: 'Demo: recording. Open the session',
  ready: 'Watch the demo',
  no_demo: 'Demo: skipped. Open the session',
  failed: 'Demo: failed. Open the session'
};

/**
 * The pull request's demo as a status row, like its checks: where it stands while cubic films it,
 * and why there is none when cubic filmed none. With a video it reveals the demo card and plays it.
 */
export function PrV2DemoStatus(t0) {
  const $ = _c(22);
  const {
    owner,
    repo,
    number,
    installationId,
    className
  } = t0;
  let t1;
  if ($[0] !== installationId) {
    t1 = {
      installationId
    };
    $[0] = installationId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data
  } = usePrV2Demo(owner, repo, number, t1);
  const [, setIsCollapsed] = usePrV2DemoCollapsed();
  const demo = data?.demo;
  if (!demo) {
    return null;
  }
  let t2;
  if ($[2] !== setIsCollapsed) {
    t2 = () => {
      setIsCollapsed(false);
      requestAnimationFrame(_temp2);
    };
    $[2] = setIsCollapsed;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleWatch = t2;
  const label = ROW_LABELS[demo.state];
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="flex items-center gap-1.5"><span className={SIDEBAR_HEADING_CLASS}>Demo</span><PrV2DemoInternalPill className="px-1 py-0 text-[10px]" /><ChevronRight className="size-4 text-zinc-500 opacity-0 transition-opacity group-hover/demo:opacity-100 group-focus-visible/demo:opacity-100" aria-hidden="true" /></span>;
    t4 = cn("flex shrink-0 items-center gap-1", SIDEBAR_HEADING_VALUE_CLASS);
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <><Play className="size-3" aria-hidden="true" />Watch</>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== demo) {
    t6 = <>{t3}<span className={t4}><PrV2DemoProgress demo={demo} ready={t5} /></span></>;
    $[7] = demo;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const row = t6;
  let t7;
  if ($[9] !== demo.sessionId || $[10] !== demo.state || $[11] !== handleWatch || $[12] !== label || $[13] !== row) {
    t7 = <h3>{demo.state === "ready" ? <button type="button" className={ROW_CLASS} onClick={handleWatch} aria-label={label}>{row}</button> : <Link href={`/code/${demo.sessionId}`} className={ROW_CLASS} target="_blank" rel="noopener noreferrer" aria-label={`${label} (opens the session in a new tab)`}>{row}</Link>}</h3>;
    $[9] = demo.sessionId;
    $[10] = demo.state;
    $[11] = handleWatch;
    $[12] = label;
    $[13] = row;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== demo.reason || $[16] !== demo.state) {
    t8 = demo.state === "no_demo" && demo.reason ? <BetterTooltip content={demo.reason} side="bottom" align="start" contentClassName="max-w-sm cursor-default whitespace-pre-wrap text-xs leading-relaxed"><p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{demo.reason}</p></BetterTooltip> : null;
    $[15] = demo.reason;
    $[16] = demo.state;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== className || $[19] !== t7 || $[20] !== t8) {
    t9 = <section className={className} aria-label="Demo">{t7}{t8}</section>;
    $[18] = className;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function _temp2() {
  const card = document.getElementById(PR_V2_DEMO_CARD_ID);
  card?.scrollIntoView({
    behavior: "smooth",
    block: "nearest"
  });
  card?.querySelector("video")?.play().catch(_temp);
}
function _temp() {}